import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.tsx'
import { useLanguage } from '../contexts/LanguageContext.tsx'
import { btnPrimaryBlock } from '../lib/ui'

export default function Register() {
  const { register } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await register(email, password, displayName)
      void navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth_register_error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-gray-950">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 w-full max-w-md dark:bg-gray-900 dark:border-gray-700">
        <div className="mb-4">
          <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">Zeni</span>
          <h1 className="mt-2 text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth_register_title')}</h1>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-950 dark:border-red-800 dark:text-red-400">
            {error}
          </div>
        )}
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('auth_display_name')}</label>
            <input
              type="text"
              data-testid="register-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              maxLength={50}
              autoComplete="name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('auth_email')}</label>
            <input
              type="email"
              data-testid="register-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('auth_password')}</label>
            <input
              type="password"
              data-testid="register-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('auth_password_hint')}</p>
          </div>
          <button
            type="submit"
            data-testid="register-submit"
            disabled={loading}
            className={btnPrimaryBlock}
          >
            {loading ? t('auth_register_submitting') : t('auth_register_submit')}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
          {t('auth_have_account')}{' '}
          <Link to="/login" className="text-indigo-600 hover:underline font-medium dark:text-indigo-400">{t('auth_to_login')}</Link>
        </p>
      </div>
    </div>
  )
}
